import { isEmpty } from 'ramda'

import { isBetween } from './utils'

export type DateModel = { day: string; month: string; year: string }

const currentYear = new Date().getFullYear() + 100

export const dateConfig: Record<
  'day' | 'month' | 'year',
  {
    min: number
    max: number
    name: string
    charCount: number
  }
> = {
  day: {
    min: 1,
    max: 31,
    name: 'day',
    charCount: 2,
  },
  month: {
    min: 1,
    max: 12,
    name: 'month',
    charCount: 2,
  },
  year: {
    min: 1,
    max: currentYear,
    name: 'year',
    charCount: 4,
  },
}

export function validateDateParts(payload: DateModel): {
  [K in keyof DateModel]: boolean
} {
  const isMonthValid = isBetween(
    Number(payload.month),
    dateConfig.month.min,
    dateConfig.month.max
  )

  const isYearValid =
    payload.year.length === dateConfig.year.charCount &&
    isBetween(Number(payload.year), dateConfig.year.min, dateConfig.year.max)

  const date = new Date(
    Number(payload.year),
    Number(payload.month) - 1,
    Number(payload.day)
  )

  const isDayValid =
    isBetween(Number(payload.day), dateConfig.day.min, dateConfig.day.max) &&
    date.getDate() === Number(payload.day)

  return {
    day: !isDayValid,
    month: !isMonthValid,
    year: !isYearValid,
  }
}

export function serializeParsedDate({ day, month, year }: DateModel): string {
  const dateString = `${year.padStart(
    dateConfig.year.charCount,
    '0'
  )}-${month.padStart(dateConfig.month.charCount, '0')}-${day.padStart(
    dateConfig.day.charCount,
    '0'
  )}`

  if ([day, month, year].every(isEmpty)) {
    return ''
  }

  return dateString
}

export function parseDateString(date = ''): DateModel {
  try {
    const dateParts = date.split('-')
    const day = Number(dateParts[2])
    const month = Number(dateParts[1])
    const year = Number(dateParts[0])
    const dateObject = new Date(year, month - 1, day)

    if (
      !(dateObject instanceof Date) ||
      dateObject.getDate() !== Number(dateParts[2]) ||
      dateObject.getMonth() + 1 !== Number(dateParts[1]) ||
      dateObject.getFullYear() !== Number(dateParts[0])
    ) {
      return { day: '', month: '', year: '' }
    }

    return {
      day: String(dateObject.getDate()),
      month: String(dateObject.getMonth() + 1),
      year: String(dateObject.getFullYear()),
    }
  } catch (e) {
    return { day: '', month: '', year: '' }
  }
}
